import { useState, type FormEvent } from 'react';
import { EmailOnlySchema } from '@kaeliux/shared';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { authErrorMessage } from '../../services/authErrors';

export interface EmailRequestFormProps {
    submitLabel: string;
    onSubmit: (email: string) => Promise<void>;
}

/** A single email field: password reset, sign-in link requests and confirming a sign-in link's address. */
export default function EmailRequestForm({ submitLabel, onSubmit }: EmailRequestFormProps) {
    const [email, setEmail] = useState('');
    const [fieldError, setFieldError] = useState<string>();
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        const parsed = EmailOnlySchema.safeParse({ email });
        if (!parsed.success) {
            setFieldError(parsed.error.issues[0]?.message);
            return;
        }

        setFieldError(undefined);
        setError(null);
        setSubmitting(true);
        try {
            await onSubmit(parsed.data.email);
        } catch (err) {
            setError(authErrorMessage(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form noValidate onSubmit={handleSubmit} className="flex flex-col gap-3">
            {error && (
                <p className="text-status-danger text-body" role="alert">
                    {error}
                </p>
            )}
            <Input
                label="Email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                error={fieldError}
            />
            <Button type="submit" size="lg" fullWidth loading={submitting}>
                {submitLabel}
            </Button>
        </form>
    );
}
